import { login, logout, getInfo } from '@/api/auth';
import { removeToken, getToken, setToken } from '@/utils/auth';
import router, { resetRouter } from '@/router';
import store from '@/store';

const state = {
  id: null,
  user: null,
  controleur: null,
  token: getToken(),
  username: '',
  email: '',
  avatar: '',
  introduction: '',
  roles: [],
  permissions: [],
};
const mutations = {
  SET_ID: (state, id) => {
    state.id = id;
  },
  SET_TOKEN: (state, token) => {
    state.token = token;
  },
  SET_INTRODUCTION: (state, introduction) => {
    state.introduction = introduction;
  },
  SET_NAME: (state, name) => {
    state.name = name;
  },
  SET_EMAIL: (state, email) => {
    state.email = email;
  },
  SET_AVATAR: (state, avatar) => {
    state.avatar = avatar;
  },
  SET_ROLES: (state, roles) => {
    state.roles = roles;
  },
  SET_PERMISSIONS: (state, permissions) => {
    state.permissions = permissions;
  },
  SET_USER: (state, user) => {
    state.user = user;
  },
  SET_CONTROLEUR: (state, controleur) => {
    state.controleur = controleur;
  },
};

/**
 * Extrait le jeton Sanctum depuis la réponse POST /login
 * (clé racine, UserResource, enveloppes data multiples, proxies).
 */
function extractLoginToken(payload, depth = 0) {
  if (!payload || typeof payload !== 'object' || depth > 5) {
    return '';
  }
  if (typeof payload.token === 'string' && payload.token.length > 1) {
    return payload.token;
  }
  if (payload.data !== undefined && payload.data !== null) {
    const nested = extractLoginToken(payload.data, depth + 1);
    if (nested) {
      return nested;
    }
  }
  return '';
}

const actions = {
  // user login
  login({ commit }, userInfo) {
    const { email, password } = userInfo;
    return new Promise((resolve, reject) => {
      login({ email: email.trim(), password: password })
        .then((response) => {
          const token = extractLoginToken(response);

          if (token) {
            commit('SET_TOKEN', token);
            setToken(token);
            resolve();
            return;
          }
          reject(new Error('Réponse de connexion sans jeton d\'authentification.'));
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  // get user info
  getInfo({ commit, state }) {
    return new Promise((resolve, reject) => {
      getInfo()
        .then((response) => {
          /**
           * L'intercepteur axios retourne déjà `response.data`, mais suivant les endpoints
           * le backend peut encore envelopper la ressource dans `data`. On homogénéise ici.
           */
          const payload = response && response.data ? response.data : response;
          const data = payload && payload.data ? payload.data : payload;

          if (!data) {
            reject('Verification failed, please Login again.');
            return;
          }

          const {
            roles,
            username,
            avatar,
            introduction,
            permissions,
            id,
            email,
            controleur,
          } = data;
          // roles must be a non-empty array
          if (!roles || roles.length <= 0) {
            reject('getInfo: roles must be a non-null array!');
          }

          commit('SET_ROLES', roles);
          commit('SET_PERMISSIONS', permissions);
          commit('SET_NAME', username);
          commit('SET_EMAIL', email);
          commit('SET_AVATAR', avatar);
          commit('SET_INTRODUCTION', introduction);
          commit('SET_ID', id);
          commit('SET_USER', { ...data, role: roles.join(' | ') });
          commit('SET_CONTROLEUR', controleur || null);

          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // user logout
  logout({ commit }) {
    return new Promise((resolve, reject) => {
      logout()
        .then(() => {
          commit('SET_TOKEN', '');
          commit('SET_ROLES', []);
          removeToken();
          resetRouter();
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // remove token
  resetToken({ commit }) {
    return new Promise((resolve) => {
      commit('SET_TOKEN', '');
      commit('SET_ROLES', []);
      removeToken();
      resolve();
    });
  },

  // Dynamically modify permissions
  changeRoles({ commit, dispatch }, role) {
    return new Promise((resolve) => {
      // const token = role + '-token';

      // commit('SET_TOKEN', token);
      // setLogged(token);

      // const { roles } = await dispatch('getInfo');

      const roles = [role.name];
      const permissions = role.permissions.map((permission) => permission.name);
      commit('SET_ROLES', roles);
      commit('SET_PERMISSIONS', permissions);
      resetRouter();

      // generate accessible routes map based on roles
      const accessRoutes = store.dispatch('permission/generateRoutes', {
        roles,
        permissions,
        isSuperviseurGrade: store.getters.isSuperviseurGrade,
      });

      // dynamically add accessible routes
      router.addRoutes(accessRoutes);

      resolve();
    });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
